import React, { useState, useEffect } from 'react';
import { goBackOrHome } from '@/lib/navHelpers';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Upload, Play, Headphones, Video, Trash2, Plus, Music, Shield, AlertTriangle, ArrowLeft, Home } from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import MeditationPlayer from '@/components/MeditationPlayer';
import { Link, useNavigate } from 'react-router-dom';
import { AppHeader } from '@/components/AppHeader';
import { User } from '@supabase/supabase-js';

interface Meditation {
  id: string;
  title: string;
  description: string;
  category: string;
  audio_url: string | null;
  video_url: string | null;
  thumbnail_url: string | null;
  duration_minutes: number | null;
  difficulty_level: string;
  meditation_type: string;
  tags: string[];
  is_published: boolean;
  play_count: number;
  created_at: string;
  updated_at: string;
}

const Meditations = () => {
  const navigate = useNavigate();
  const [meditations, setMeditations] = useState<Meditation[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedMeditation, setSelectedMeditation] = useState<Meditation | null>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general',
    difficulty_level: 'beginner',
    meditation_type: 'guided',
    duration_minutes: '',
    tags: '',
  });

  useEffect(() => {
    checkAdminStatus();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadMeditations();
    }
  }, [isAdmin]);

  const checkAdminStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setCheckingAdmin(false);
        return;
      }

      setCurrentUser(user); // Store user for displaying ID

      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();

      if (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      } else {
        setIsAdmin(!!data);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    } finally {
      setCheckingAdmin(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const loadMeditations = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('meditations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMeditations(data || []);
    } catch (error) {
      console.error('Error loading meditations:', error);
      toast.error('Failed to load meditations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMeditation = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const meditationData = {
        user_id: user.id,
        title: formData.title,
        description: formData.description,
        category: formData.category,
        difficulty_level: formData.difficulty_level,
        meditation_type: formData.meditation_type,
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()) : [],
        is_published: false
      };

      const { data, error } = await supabase
        .from('meditations')
        .insert([meditationData])
        .select()
        .single();

      if (error) throw error;

      setMeditations([data, ...meditations]);
      setShowCreateDialog(false);
      setFormData({
        title: '',
        description: '',
        category: 'general',
        difficulty_level: 'beginner',
        meditation_type: 'guided',
        duration_minutes: '',
        tags: '',
      });
      toast.success('Meditation created successfully!');
    } catch (error) {
      console.error('Error creating meditation:', error);
      toast.error('Failed to create meditation');
    }
  };

  const handleFileUpload = async (file: File, meditationId: string, type: 'audio' | 'video') => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      if (type === 'audio') setUploadingAudio(true);
      if (type === 'video') setUploadingVideo(true);

      const bucket = type === 'audio' ? 'meditation-audio' : 'meditation-videos';
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${meditationId}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(fileName);

      const updateField = type === 'audio' ? 'audio_url' : 'video_url';
      
      const { error: updateError } = await supabase
        .from('meditations')
        .update({ [updateField]: publicUrl } as any)
        .eq('id', meditationId);

      if (updateError) throw updateError;

      // Update local state
      setMeditations(meditations.map(meditation => 
        meditation.id === meditationId 
          ? { ...meditation, [updateField]: publicUrl }
          : meditation
      ));

      toast.success(`${type === 'audio' ? 'Audio' : 'Video'} uploaded successfully!`);
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      toast.error(`Failed to upload ${type}`);
    } finally {
      if (type === 'audio') setUploadingAudio(false);
      if (type === 'video') setUploadingVideo(false);
    }
  };

  const handleDeleteMeditation = async (meditationId: string) => {
    if (!confirm('Are you sure you want to delete this meditation?')) return;

    try {
      const { error } = await supabase
        .from('meditations')
        .delete()
        .eq('id', meditationId);

      if (error) throw error;

      setMeditations(meditations.filter(meditation => meditation.id !== meditationId));
      toast.success('Meditation deleted successfully!');
    } catch (error) {
      console.error('Error deleting meditation:', error);
      toast.error('Failed to delete meditation');
    }
  };

  const togglePublishStatus = async (meditationId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('meditations')
        .update({ is_published: !currentStatus })
        .eq('id', meditationId);

      if (error) throw error;

      setMeditations(meditations.map(meditation => 
        meditation.id === meditationId 
          ? { ...meditation, is_published: !currentStatus }
          : meditation
      ));

      toast.success(`Meditation ${!currentStatus ? 'published' : 'unpublished'} successfully!`);
    } catch (error) {
      console.error('Error updating meditation status:', error);
      toast.error('Failed to update meditation status');
    }
  };

  if (checkingAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full p-8">
          <div className="text-center mb-6">
            <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-light text-foreground mb-4">Admin Access Required</h2>
            <p className="text-muted-foreground mb-6">
              You need admin privileges to upload and manage meditations.
            </p>
          </div>

          {currentUser && (
            <div className="mb-6 p-4 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-2">Your User ID:</p>
              <div className="bg-background p-3 rounded border font-mono text-sm break-all">
                {currentUser.id}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Copy this ID and use it in the SQL query below
              </p>
            </div>
          )}

          <div className="mb-6 p-4 bg-muted rounded-lg">
            <p className="text-sm font-medium mb-3">Run this SQL query in Supabase:</p>
            <div className="bg-background p-3 rounded border font-mono text-sm">
              <div>INSERT INTO public.user_roles (user_id, role)</div>
              <div>VALUES ('{currentUser?.id || 'YOUR_USER_ID_HERE'}', 'admin'::app_role);</div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Replace YOUR_USER_ID_HERE with your actual User ID from above if not filled automatically
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => window.open(`https://supabase.com/dashboard/project/jbdsdzmyqkfbqijnoqmo/sql/new`, '_blank')}
              className="flex-1"
            >
              <Shield className="h-4 w-4 mr-2" />
              Open Supabase SQL Editor
            </Button>
            <Link to="/meditation-library" className="flex-1">
              <Button variant="outline" className="w-full">
                <Music className="h-4 w-4 mr-2" />
                Browse Meditation Library
              </Button>
            </Link>
          </div>
          
          <p className="text-sm text-muted-foreground mt-4 text-center">
            After running the SQL query, refresh this page to access the admin interface.
          </p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader user={currentUser} onSignOut={handleSignOut} />
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-6xl mx-auto">
          {/* Navigation */}
          <div className="flex items-center gap-2 mb-6">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => goBackOrHome(navigate)}
              className="flex items-center gap-2"
            >
              <Home className="h-4 w-4" />
              <span className="hidden sm:inline">Home</span>
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2"
            >
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">Admin Portal</span>
            </Button>
          </div>

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light text-foreground">Admin: Meditation Management</h1>
              </div>
              <p className="text-sm sm:text-base lg:text-lg text-muted-foreground">Upload MP3 meditations and hypnotic videos for your users to enjoy.</p>
            </div>
            
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Create Meditation
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create New Meditation</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateMeditation} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Meditation Title</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      placeholder="Enter meditation title"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Describe your meditation"
                      rows={3}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="category">Category</Label>
                      <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="sleep">Sleep</SelectItem>
                          <SelectItem value="anxiety">Anxiety Relief</SelectItem>
                          <SelectItem value="focus">Focus & Concentration</SelectItem>
                          <SelectItem value="healing">Healing</SelectItem>
                          <SelectItem value="spiritual">Spiritual</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="difficulty">Difficulty</Label>
                      <Select value={formData.difficulty_level} onValueChange={(value) => setFormData({...formData, difficulty_level: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="beginner">Beginner</SelectItem>
                          <SelectItem value="intermediate">Intermediate</SelectItem>
                          <SelectItem value="advanced">Advanced</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="type">Meditation Type</Label>
                      <Select value={formData.meditation_type} onValueChange={(value) => setFormData({...formData, meditation_type: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="guided">Guided</SelectItem>
                          <SelectItem value="hypnosis">Hypnosis</SelectItem>
                          <SelectItem value="visualization">Visualization</SelectItem>
                          <SelectItem value="breathwork">Breathwork</SelectItem>
                          <SelectItem value="soundscape">Soundscape</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="duration">Duration (minutes)</Label>
                      <Input
                        id="duration"
                        type="number"
                        value={formData.duration_minutes}
                        onChange={(e) => setFormData({...formData, duration_minutes: e.target.value})}
                        placeholder="e.g., 20"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="tags">Tags (comma-separated)</Label>
                    <Input
                      id="tags"
                      value={formData.tags}
                      onChange={(e) => setFormData({...formData, tags: e.target.value})}
                      placeholder="relaxation, sleep, healing"
                    />
                  </div>
                  
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit">Create Meditation</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Meditations Grid */}
          {meditations.length === 0 ? (
            <Card className="p-12 text-center">
              <Music className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No meditations yet</h3>
              <p className="text-muted-foreground mb-4">Create your first guided meditation to get started.</p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Meditation
              </Button>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {meditations.map((meditation) => (
                <Card key={meditation.id} className="p-6 shadow-therapy border-0">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-foreground mb-2">{meditation.title}</h3>
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{meditation.description}</p>
                      
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <Badge variant="secondary">{meditation.category}</Badge>
                        <Badge variant="outline">{meditation.meditation_type}</Badge>
                        <Badge variant="outline">{meditation.difficulty_level}</Badge>
                        {meditation.is_published && <Badge className="bg-green-100 text-green-800">Published</Badge>}
                      </div>
                      
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        {meditation.duration_minutes && <span>{meditation.duration_minutes} min</span>}
                        <span>{meditation.play_count} plays</span>
                      </div>
                    </div>
                    
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => togglePublishStatus(meditation.id, meditation.is_published)}
                      >
                        {meditation.is_published ? 'Unpublish' : 'Publish'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteMeditation(meditation.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Audio Upload Section */}
                  <div className="space-y-3">
                    <div className="border-2 border-dashed border-border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Headphones className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium">MP3 Audio</span>
                      </div>
                      
                      {meditation.audio_url ? (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Audio uploaded</span>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => setSelectedMeditation(meditation)}
                          >
                            <Play className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <div>
                          <input
                            type="file"
                            accept="audio/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleFileUpload(file, meditation.id, 'audio');
                            }}
                            className="hidden"
                            id={`audio-${meditation.id}`}
                          />
                          <label htmlFor={`audio-${meditation.id}`}>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="w-full" 
                              disabled={uploadingAudio}
                              asChild
                            >
                              <span>
                                <Upload className="h-3 w-3 mr-1" />
                                {uploadingAudio ? 'Uploading...' : 'Upload MP3'}
                              </span>
                            </Button>
                          </label>
                        </div>
                      )}
                    </div>
                    
                    {/* Video Upload Section */}
                    <div className="border-2 border-dashed border-border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Video className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium">Hypnotic Video</span>
                      </div>
                      
                      {meditation.video_url ? (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Video uploaded</span>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => setSelectedMeditation(meditation)}
                          >
                            <Play className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <div>
                          <input
                            type="file"
                            accept="video/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleFileUpload(file, meditation.id, 'video');
                            }}
                            className="hidden"
                            id={`video-${meditation.id}`}
                          />
                          <label htmlFor={`video-${meditation.id}`}>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="w-full"
                              disabled={uploadingVideo}
                              asChild
                            >
                              <span>
                                <Upload className="h-3 w-3 mr-1" />
                                {uploadingVideo ? 'Uploading...' : 'Upload Video'}
                              </span>
                            </Button>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Meditation Player Modal */}
      {selectedMeditation && (
        <MeditationPlayer
          title={selectedMeditation.title}
          audioUrl={selectedMeditation.audio_url || undefined}
          videoUrl={selectedMeditation.video_url || undefined}
          onClose={() => setSelectedMeditation(null)}
        />
      )}
    </div>
  );
};

export default Meditations;